import type { FocusEventHandler } from "react";

import { type Control, Controller } from "react-hook-form";

import { Input } from "@alpacahq/ui/old";

import { ToggleOptions } from "~/v2/components/common/ToggleOptions";
import type { CryptoQuantityType } from "~/v2/constants/cryptoOrder";
import type { CryptoOrderTicketSchema } from "../schema";
import { createClampedChangeHandler } from "../utils";

interface QuantityInputProps {
  control: Control<CryptoOrderTicketSchema>;
  isQuantityTypeDisabled: boolean;
  isNotionalUsd: boolean;
  quantityType: CryptoQuantityType;
  quantityLabel: string;
  quantityTypeOptions: { label: string; value: CryptoQuantityType }[];
  quantityStep: string;
  quantityMaxDecimals: number | undefined;
  onQuantityTypeChange: (quantityType: CryptoQuantityType) => void;
  onQuantityBlur: FocusEventHandler<HTMLInputElement>;
  availableBase: string | undefined;
  availableQuote: string | undefined;
  baseCurrency: string;
  quoteCurrency: string;
  isUsdPair: boolean;
}

export const QuantityInput = ({
  control,
  isQuantityTypeDisabled,
  isNotionalUsd,
  quantityType,
  quantityLabel,
  quantityTypeOptions,
  quantityStep,
  quantityMaxDecimals,
  onQuantityTypeChange,
  onQuantityBlur,
  availableBase,
  availableQuote,
  baseCurrency,
  quoteCurrency,
  isUsdPair,
}: QuantityInputProps) => (
  <div>
    <div className="mb-1 flex items-center justify-between gap-1">
      <label htmlFor="quantity" className="font-semibold text-gray-700 text-sm">
        {quantityLabel}
      </label>
      <div className="flex items-center justify-end gap-1">
        {!isQuantityTypeDisabled && (
          <ToggleOptions
            options={quantityTypeOptions}
            value={quantityType}
            onChange={onQuantityTypeChange}
            size="xs"
          />
        )}
      </div>
    </div>
    <Controller
      name="quantity"
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Input
          id="quantity"
          type="number"
          min="0"
          step={quantityStep}
          {...field}
          onChange={createClampedChangeHandler(
            quantityMaxDecimals,
            field.onChange,
          )}
          onBlur={(event) => {
            field.onBlur();
            onQuantityBlur(event);
          }}
          errorMsg={error?.message}
          leftIcon={isNotionalUsd ? <span>$</span> : undefined}
          rightIcon={
            !isNotionalUsd ? (
              <span className="font-medium text-xs">
                {quantityType === "base" ? baseCurrency : quoteCurrency}
              </span>
            ) : undefined
          }
        />
      )}
    />
    <div className="mt-1.5 flex flex-col gap-1">
      {availableBase && (
        <div className="flex items-center justify-between gap-2">
          <p className="m-0 shrink-0 p-0 text-gray-600 text-xs">
            Available {baseCurrency}
          </p>
          <p className="m-0 min-w-0 break-all p-0 text-right font-medium text-gray-800 text-xs">
            {availableBase}
          </p>
        </div>
      )}
      {availableQuote && !isUsdPair && (
        <div className="flex items-center justify-between gap-2">
          <p className="m-0 shrink-0 p-0 text-gray-600 text-xs">
            Available {quoteCurrency}
          </p>
          <p className="m-0 min-w-0 break-all p-0 text-right font-medium text-gray-800 text-xs">
            {availableQuote}
          </p>
        </div>
      )}
    </div>
  </div>
);
